import { useAppKitNetwork } from '@reown/appkit/react';
import { useCallback } from 'react';
import { useAccount, useChainId } from 'wagmi';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    isAnchorChainId,
    type AnchorChainId,
} from '../config/anchors';
import { normalizeChainId } from '../config/networks';
import { useWalletStore } from '../store';

export interface AnchorChainSelection {
    /** The anchor everything this app signs and reads is scoped to. */
    anchorChainId: AnchorChainId;
    /** Record an explicit choice (the selector). Persists as the last used one. */
    selectAnchorChain: (chainId: AnchorChainId) => void;
}

/**
 * Which authorization anchor this session is on.
 *
 * Resolution order, strongest first:
 *   1. an explicit pick (the trading-session / onboarding selector),
 *   2. the connected wallet's chain when that chain IS an anchor — a user
 *      sitting on Robinhood means Robinhood, without a second click. The
 *      wallet's LIVE chain (wagmi's account chain, read from the connector)
 *      comes first: AppKit's active network and wagmi's config chain are both
 *      persisted across reloads, so after a reconnect they can still say
 *      Cronos while MetaMask already sits on Robinhood,
 *   3. the anchor they last used (persisted),
 *   4. Cronos, which is what "no anchor" has always meant.
 *
 * The wallet's chain deliberately does NOT win over an explicit pick: a
 * withdrawal switches the wallet to HyperEVM and back, and a deposit may sit on
 * a third chain entirely, so an inferred anchor must never quietly move an
 * identity the user already chose.
 */
export function useAnchorChain(): AnchorChainSelection {
    const storedAnchorChainId = useWalletStore((state) => state.anchorChainId);
    const pickedByUser = useWalletStore((state) => state.anchorPickedByUser);
    const setAnchorChain = useWalletStore((state) => state.setAnchorChain);
    const { chainId: appKitChainId } = useAppKitNetwork();
    const { chainId: accountChainId } = useAccount();
    const wagmiChainId = useChainId();

    const connectedChainId =
        accountChainId ?? normalizeChainId(appKitChainId) ?? wagmiChainId ?? undefined;

    let anchorChainId: AnchorChainId;
    if (pickedByUser && isAnchorChainId(storedAnchorChainId)) {
        anchorChainId = storedAnchorChainId;
    } else if (isAnchorChainId(connectedChainId)) {
        anchorChainId = connectedChainId as AnchorChainId;
    } else if (isAnchorChainId(storedAnchorChainId)) {
        anchorChainId = storedAnchorChainId;
    } else {
        anchorChainId = DEFAULT_ANCHOR_CHAIN_ID;
    }

    const selectAnchorChain = useCallback(
        (chainId: AnchorChainId) => setAnchorChain(chainId, true),
        [setAnchorChain],
    );

    return {
        anchorChainId,
        selectAnchorChain,
    };
}
